import { useMemo } from 'react';
import { ENUM_LABELS, type ReagentItemStock, type ReagentLot } from '#shared/types';
import { Card } from '#client/components/ui/Card';
import { DataTable } from '#client/components/ui/DataTable';
import { DetailList } from '#client/components/ui/DetailList';
import { EmptyState } from '#client/components/ui/EmptyState';
import { ResourceDetailPage } from '#client/components/ui/ResourceDetailPage';
import { RESOURCE } from '#client/lib/routes';
import { reagentLotColumns } from './inventory-columns';
import { reagentStockDetails } from './reagent-item-detail.logic';
import { useAllRecords } from '#client/hooks/useAllRecords';

const PATH = RESOURCE.reagentItems;

export function ReagentItemDetailPage() {
  return (
    <ResourceDetailPage<ReagentItemStock>
      path={PATH}
      noun="reagent"
      editCapability="reagentItem.edit"
      deleteCapability="reagentItem.delete"
      title={(item) => item.name}
      subtitle={(item) => `${item.department_name} · ${item.pack_label}`}
      deleteLabel={(item) => item.name}
      fullWidth
    >
      {(item) => <ReagentItemBody item={item} />}
    </ResourceDetailPage>
  );
}

function ReagentItemBody({ item }: { item: ReagentItemStock }) {
  const extraQuery = useMemo(() => ({ item_id: String(item.id) }), [item.id]);
  const lots = useAllRecords<ReagentLot>({
    path: RESOURCE.reagentLots,
    extraQuery,
  });

  const columns = reagentLotColumns(ENUM_LABELS.stockUnit[item.base_unit]);

  return (
    <div className="flex flex-col gap-5">
      <Card title="Stock">
        <DetailList items={reagentStockDetails(item)} />
      </Card>

      <Card
        title="Lots"
        subtitle="Consumed soonest-expiry first, so this is the order they will be used"
        flush
      >
        <DataTable
          columns={columns}
          rows={lots.items}
          rowKey={(row) => row.id}
          loading={lots.loading}
          caption={`Stock lots for ${item.name}`}
          stickyHeader={false}
          emptyState={
            <EmptyState
              title="No stock entered"
              body="Nothing has been received for this reagent yet, so a test that needs it will warn rather than deduct."
            />
          }
        />
      </Card>
    </div>
  );
}
